import { useForm } from '@tanstack/react-form';
import { HelpCircle } from '@untitled-ui/icons-react';
import { useEffect } from 'react';

import { Button } from '../../../../../../components/buttons/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '../../../../../../components/dialog';
import { TextField } from '../../../../../../components/inputs/text-field';
import { ToastVariant } from '../../../../../../components/toast';
import { Tooltip } from '../../../../../../components/tooltip';
import { addBloggleIntegrationReqSchema } from '../../../../../../entity/api/routes/organization';
import { BadRequestError } from '../../../../../../error';
import { convertError } from '../../../../../../error/convert';
import { trackError } from '../../../../../../error/tracking';
import { captureException } from '../../../../../../logger/capture-exception';
import { useAddBloggleIntegration } from '../../../../../../services/storage/organization-integrations-queries';
import { tGlobal, tNamespace } from '../../../../../../services/translation';
import { useToast } from '../../../../../../shared/contexts/toast-context';
import { parseFirstZodError } from '../../../../../../shared/validation';

const t = tNamespace('app.integrations');

type ConnectBloggleModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
};

export function ConnectBloggleModal(props: ConnectBloggleModalProps) {
  const { open, onOpenChange, organizationId } = props;

  const addBloggleIntegration = useAddBloggleIntegration();
  const addToast = useToast();

  const form = useForm({
    defaultValues: {
      externalName: '',
      accessKey: '',
    },
    onSubmit: function (data) {
      if (!data.formApi.state.isValid) {
        return;
      }

      addBloggleIntegration.mutate(
        {
          organizationId,
          data: {
            externalName: data.value.externalName ?? '',
            accessKey: data.value.accessKey ?? '',
          },
        },
        {
          onSuccess: function () {
            onOpenChange(false);
            form.reset();
            addToast({
              title: t('bloggleConnected'),
              variant: ToastVariant.Success,
              id: Date.now(),
            });
          },
          onError: function (err) {
            const handledErr = convertError(err);
            if (handledErr instanceof BadRequestError) {
              addToast({
                title: tGlobal('badRequest'),
                variant: ToastVariant.Error,
                id: Date.now(),
              });
              return;
            }

            captureException(
              err,
              {
                file: 'connect-bloggle-modal.tsx',
                method: 'addBloggleIntegration',
                infoMessage: 'unable to add Bloggle integration',
              },
              trackError,
              addToast
            );
          },
        }
      );
    },
  });

  useEffect(
    function () {
      if (!open) {
        form.reset();
      }
    },
    [open]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hasCloseButton className="min-w-[550px]">
        <div className="mb-6 flex w-full items-center justify-center">
          <div
            className={
              'flex size-[40px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#1C4FFD]'
            }
          >
            <img src="/bloggle-square.svg" alt="Bloggle" width={31} height={31} />
          </div>
        </div>
        <DialogTitle align="center">{t('connectBloggle')}</DialogTitle>
        <DialogDescription align="center">{t('connectBloggleDescription')}</DialogDescription>
        <form className="mt-8 flex w-full flex-col gap-6">
          <form.Field
            name="externalName"
            validators={{
              onChange: (data) =>
                parseFirstZodError(
                  addBloggleIntegrationReqSchema.shape.body.shape.externalName,
                  data.value
                ),
              onBlur: (data) =>
                parseFirstZodError(
                  addBloggleIntegrationReqSchema.shape.body.shape.externalName,
                  data.value
                ),
            }}
            // eslint-disable-next-line react/no-children-prop
            children={function (field) {
              return (
                <TextField
                  sizeField="md"
                  label={
                    <div className="flex items-center gap-2">
                      <p>{t('integrationName')}</p>
                      <Tooltip text={t('integrationNameTooltip')}>
                        <div>
                          <HelpCircle width={12} height={12} />
                        </div>
                      </Tooltip>
                    </div>
                  }
                  placeholder={t('integrationNamePlaceholder')}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className="w-full"
                  error={field.state.meta?.errors}
                />
              );
            }}
          />
          <form.Field
            name="accessKey"
            validators={{
              onChange: (data) =>
                parseFirstZodError(
                  addBloggleIntegrationReqSchema.shape.body.shape.accessKey,
                  data.value
                ),
              onBlur: (data) =>
                parseFirstZodError(
                  addBloggleIntegrationReqSchema.shape.body.shape.accessKey,
                  data.value
                ),
            }}
            // eslint-disable-next-line react/no-children-prop
            children={function (field) {
              return (
                <TextField
                  sizeField="md"
                  type="password"
                  label={
                    <div className="flex items-center gap-2">
                      <p>{t('bloggleToken')}</p>
                      <Tooltip text={t('bloggleTokenTooltip')}>
                        <div>
                          <HelpCircle width={12} height={12} />
                        </div>
                      </Tooltip>
                    </div>
                  }
                  placeholder={t('bloggleTokenPlaceholder')}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className="w-full"
                  error={field.state.meta?.errors}
                />
              );
            }}
          />
        </form>
        <DialogFooter>
          <Button
            variant="accent"
            size="xl"
            className="w-full"
            loading={addBloggleIntegration.isPending}
            onClick={() => void form.handleSubmit()}
          >
            {t('connect')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
